// Dev wallet adapter — decorators over the real EVM adapter (same ConnectionHookResult / SendTxHookResult
// contract as every adapter in src/ui/wallet). While this tab acts as a dev identity, "connected" is that
// identity and sends return a mock tx hash the local server confirms instantly; otherwise the real adapter
// result is returned untouched. Dev payments cover EVM coins (dev handles publish one EVM address).

import { useMemo } from 'react';
import { ok } from '@shared/types/result.ts';
import type { ConnectionHookResult, SendTxHookResult } from '../wallet/types.ts';
import { useDevState, activeDevIdentity, setActiveDevIdentity, isDevIdentityRestoring } from './dev-store.ts';
import type { DevIdentity } from './dev-store.ts';

export const DEV_CONNECTOR_ID = 'chainsale-dev';

export const devConnection = (identity: DevIdentity): ConnectionHookResult =>
  Object.freeze({
    account: Object.freeze({
      address: identity.address,
      isConnected: true,
      isReconnecting: false,
      chainId: undefined,
      connectorId: DEV_CONNECTOR_ID,
    }),
    openModal: () => Promise.resolve(),
    disconnect: () => setActiveDevIdentity(null),
  });

// 64 random hex chars after the server's dev prefix (unique per payment)
export const devTxHash = (prefix: string, random: () => number = Math.random): string =>
  prefix + Array.from({ length: 64 }, () => Math.floor(random() * 16).toString(16)).join('');

export const devSender = (txPrefix: string): SendTxHookResult =>
  Object.freeze({
    sendTransaction: () => Promise.resolve(ok(devTxHash(txPrefix))),
    approveFee: () => Promise.resolve(ok('approved')),
  });

// While a saved dev identity is being restored (dev-mode check in flight) the connection reports
// isReconnecting — the same signal a real wallet gives while restoring — so guards wait, not redirect.
export const restoringConnection = (real: ConnectionHookResult): ConnectionHookResult =>
  Object.freeze({ ...real, account: Object.freeze({ ...real.account, isReconnecting: true }) });

export const useDevConnection = (real: ConnectionHookResult): ConnectionHookResult => {
  const state = useDevState();
  const identity = activeDevIdentity(state);
  const restoring = isDevIdentityRestoring(state);
  return useMemo(
    () => (identity !== null ? devConnection(identity) : restoring ? restoringConnection(real) : real),
    [identity, restoring, real],
  );
};

export const useDevSendTx = (real: SendTxHookResult): SendTxHookResult => {
  const state = useDevState();
  const identity = activeDevIdentity(state);
  const prefix = state.status?.txPrefix ?? null;
  return useMemo(() => (identity === null || prefix === null ? real : devSender(prefix)), [identity, prefix, real]);
};
